import React, { useEffect } from 'react';
import { EddyProjectVisibility } from 'redux/modules/wizard-export/types';
import { AutogramVideoType, IEmbedConfig, PodcastIdentifier } from 'types';
import { useIntroOutroUploadIntegration } from '../integrations';
import useMediaUploadIntegration from '../integrations/useMediaUploadIntegration';
import useStaticTextIntegration from '../integrations/useStaticTextIntegration';
import {
  getStateContent,
  OnStateChange,
  useEditorStateReducer,
  useNavigation,
  useTemplateDispatch,
  VideoTemplateState,
} from '../state';
import { VideoTemplateEditorIntegrations } from '../types/integrations';
import { CanvasSizeProvider } from './CanvasSizeContext';
import { NavigationProvider } from './NavigationContext';
import { VideoEditorDispatchProvider } from './VideoEditorDispatchContext';
import {
  DEFAULT_FEATURES,
  VideoEditorStateProvider,
  VideoTemplateEditorFeatures,
  VideoTemplateEditorMedia,
} from './VideoEditorStateContext';
import { VideoTemplateDispatchProvider } from './VideoTemplateDispatchContext';
import { VideoTemplateStateProvider } from './VideoTemplateStateContext';

export interface VideoTemplateEditorContextProviderProps {
  addTextPlaceholder?: string;
  audioClipDurationSec?: number;
  children?: React.ReactNode;
  disabled?: boolean;
  eddyProjectVisibility?: EddyProjectVisibility;
  features?: VideoTemplateEditorFeatures;
  integrations?: VideoTemplateEditorIntegrations;
  isFullEpisode?: boolean;
  allowCaptionsManagement?: boolean;
  defaultEmbedConfig?: IEmbedConfig;
  loading?: boolean;
  onChange?: OnStateChange;
  videoType?: AutogramVideoType;
  state: VideoTemplateState;
  media?: VideoTemplateEditorMedia;
  projectName?: string;
  podcastIdentifier?: PodcastIdentifier;
  onCtaClick?: (isTemplateDirty?: boolean) => void;
}

const VideoTemplateEditorContextProvider: React.FC<VideoTemplateEditorContextProviderProps> = ({
  addTextPlaceholder,
  audioClipDurationSec,
  children,
  disabled,
  eddyProjectVisibility,
  features = DEFAULT_FEATURES,
  integrations,
  isFullEpisode,
  allowCaptionsManagement,
  defaultEmbedConfig,
  loading,
  onChange,
  state,
  media,
  projectName,
  podcastIdentifier,
}) => {
  const { canvas, container, template } = getStateContent(state);
  const defaultIntegrations = [
    useMediaUploadIntegration({ priority: 0 }),
    useStaticTextIntegration({ placeholder: addTextPlaceholder, priority: 2 }),
    useIntroOutroUploadIntegration({ priority: 3 }),
  ];

  const templateType = template?.templateType;

  const machine = useNavigation({
    pendingChildView: undefined,
    id: undefined,
    templateType,
  });
  const templateDispatch = useTemplateDispatch({ onChange });
  const [editorState, editorDispatch] = useEditorStateReducer();

  const [, , service] = machine;

  useEffect(() => {
    const subscription = service.subscribe(({ event }) => {
      templateDispatch(event);
      editorDispatch(event);
    });
    return subscription.unsubscribe;
  }, [editorDispatch, service, templateDispatch]);

  return (
    <VideoEditorStateProvider
      integrations={integrations || defaultIntegrations}
      state={editorState}
      {...{
        addTextPlaceholder,
        audioClipDurationSec,
        disabled,
        eddyProjectVisibility,
        features,
        isFullEpisode,
        allowCaptionsManagement,
        defaultEmbedConfig,
        loading,
        media,
        projectName,
        podcastIdentifier,
      }}
    >
      <VideoEditorDispatchProvider dispatch={editorDispatch}>
        <CanvasSizeProvider canvas={canvas} container={container}>
          <NavigationProvider value={machine}>
            <VideoTemplateDispatchProvider value={templateDispatch}>
              <VideoTemplateStateProvider value={state}>
                {children}
              </VideoTemplateStateProvider>
            </VideoTemplateDispatchProvider>
          </NavigationProvider>
        </CanvasSizeProvider>
      </VideoEditorDispatchProvider>
    </VideoEditorStateProvider>
  );
};

export default VideoTemplateEditorContextProvider;
